// src/components/Dashboard/AdjusterShell.jsx
//
// Marco del panel para roles sin admin (ajustador, P.A Admin, asistentes):
// barra superior con la empresa, pestañas y "Nuevo reclamo"; en móvil, una
// barra fija abajo con las secciones principales para que el menú siempre
// esté a la vista. "Mi cuenta" (avatar) reúne perfil, idioma/tema, salir y
// las secciones que no caben abajo.
import React, { useContext } from 'react';
import { LanguageContext } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import CompanyLogos from '../CompanyLogos';
import WorkspacePreferences from '../WorkspacePreferences';
import './AdjusterShell.css';

// Pestañas según el rol (ver permissions.js):
// - P.A y asistente: Inicio con sus números y pendientes.
// - P.A Admin: entra al Resumen de su compañía y a Reclamos (sin Inicio).
// - CSR Assistant: entra a Reclamos, con selector de empresa; sin dinero.
// `bottom` = va en la barra inferior del móvil (máx. 4 + "Nuevo").
export const homeTabFor = roleKey => (roleKey === 'pa_admin' ? 'overview' : roleKey === 'csr_assistant' ? 'claimstatus' : 'home');

export const adjusterTabs = (can, roleKey) => {
  const claims = { id: 'claimstatus', icon: 'fa-solid fa-folder-open', es: 'Reclamos', en: 'Claims', bottom: true };
  const schedule = { id: 'schedule', icon: 'fa-regular fa-calendar', es: 'Agenda', en: 'Schedule', bottom: true };
  const activity = { id: 'activity', icon: 'fa-solid fa-bell', es: 'Actividad', en: 'Activity', bottom: true };
  const commissions = can('viewCheckCommissions') && { id: 'commissions', icon: 'fa-solid fa-coins', es: 'Comisiones', en: 'Commissions' };
  const docusign = can('assignDocusign') && { id: 'docusign', icon: 'fa-solid fa-file-signature', es: 'Avisos de firma', en: 'Signature notices', short: ['Firmas', 'Signatures'] };
  const users = can('manageUsers') && { id: 'users', icon: 'fa-solid fa-users', es: 'Usuarios', en: 'Users' };
  const cuts = can('viewPaymentCuts') && { id: 'cuts', icon: 'fa-solid fa-file-invoice-dollar', es: 'Cortes de pagos', en: 'Payment cuts', short: ['Cortes', 'Cuts'] };
  if (roleKey === 'pa_admin') {
    return [{ id: 'overview', icon: 'fa-solid fa-chart-line', es: 'Resumen', en: 'Overview', bottom: true }, claims, commissions, cuts, schedule, activity, docusign, users].filter(Boolean);
  }
  if (roleKey === 'csr_assistant') {
    return [claims, docusign && { ...docusign, bottom: true }, schedule, activity, users].filter(Boolean);
  }
  return [
    { id: 'home', icon: 'fa-solid fa-house', es: 'Inicio', en: 'Home', bottom: true },
    { ...claims, es: 'Mis reclamos', en: 'My claims', short: ['Reclamos', 'Claims'] },
    commissions,
    can('viewOverview') && { id: 'overview', icon: 'fa-solid fa-chart-line', es: 'Resumen', en: 'Overview' },
    schedule, activity, docusign, users
  ].filter(Boolean);
};

const AdjusterShell = ({ activeTab, onNavigate, companyLogo, companyName, children }) => {
  const { language } = useContext(LanguageContext);
  const es = language === 'es';
  const { currentUser, userAdjusterName, userCompany, userRoleKey, can } = useAuth();
  const tabs = adjusterTabs(can, userRoleKey);
  const label = tab => (es ? tab.es : tab.en);
  const name = userAdjusterName || currentUser?.displayName || currentUser?.email || '';
  const initials = name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
  const bottomTabs = tabs.filter(tab => tab.bottom);
  // El CSR Assistant trabaja para CSR con varias compañías: la barra no muestra una.
  const csrAssistant = userRoleKey === 'csr_assistant';
  const company = csrAssistant ? 'CSR Claims Assistant' : companyName || userCompany;

  return (
    <div className="as-shell">
      <header className="as-top">
        <button type="button" className="as-brand" onClick={() => onNavigate(homeTabFor(userRoleKey))}>
          {csrAssistant ? <i className="fa-solid fa-shield-halved" aria-hidden="true" /> : <CompanyLogos company={company} src={companyLogo} className="as-logo" />}
          <span className="as-brand-text"><strong>{company}</strong><small>{csrAssistant ? (es ? 'Asistente CSR' : 'CSR Assistant') : (es ? 'Estado de reclamos' : 'Claim status')}</small></span>
        </button>
        <nav className="as-tabs" aria-label={es ? 'Secciones' : 'Sections'}>
          {tabs.map(tab => (
            <button key={tab.id} type="button" className={`as-tab ${activeTab === tab.id ? 'is-active' : ''}`} aria-current={activeTab === tab.id ? 'page' : undefined} onClick={() => onNavigate(tab.id)}>
              {label(tab)}
            </button>
          ))}
        </nav>
        <div className="as-actions">
          <WorkspacePreferences compact />
          <button type="button" className="as-new" onClick={() => onNavigate('create')}>
            <i className="fa-solid fa-plus" aria-hidden="true" /> <span>{es ? 'Nuevo reclamo' : 'New claim'}</span>
          </button>
          <button type="button" className={`as-avatar ${activeTab === 'account' ? 'is-active' : ''}`} onClick={() => onNavigate('account')} aria-label={es ? 'Mi cuenta' : 'My account'} title={es ? 'Mi cuenta' : 'My account'}>
            {initials || <i className="fa-solid fa-user" aria-hidden="true" />}
          </button>
        </div>
      </header>

      <main className="as-content">{children}</main>

      <nav className="as-bottom" aria-label={es ? 'Secciones principales' : 'Main sections'}>
        {bottomTabs.slice(0, 2).map(tab => (
          <button key={tab.id} type="button" className={activeTab === tab.id ? 'is-active' : ''} aria-current={activeTab === tab.id ? 'page' : undefined} onClick={() => onNavigate(tab.id)}>
            <i className={tab.icon} aria-hidden="true" /><span>{tab.short ? tab.short[es ? 0 : 1] : label(tab)}</span>
          </button>
        ))}
        <button type="button" className={`as-bottom-new ${activeTab === 'create' ? 'is-active' : ''}`} onClick={() => onNavigate('create')}>
          <span className="as-bottom-plus"><i className="fa-solid fa-plus" aria-hidden="true" /></span><span>{es ? 'Nuevo' : 'New'}</span>
        </button>
        {bottomTabs.slice(2, 4).map(tab => (
          <button key={tab.id} type="button" className={activeTab === tab.id ? 'is-active' : ''} aria-current={activeTab === tab.id ? 'page' : undefined} onClick={() => onNavigate(tab.id)}>
            <i className={tab.icon} aria-hidden="true" /><span>{tab.short ? tab.short[es ? 0 : 1] : label(tab)}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default AdjusterShell;
